import React from "react";
import { useIntl, defineMessages } from "react-intl";
import "./paymentPlanSummaryCard.scss";
import {
  formatFullTestNames,
  getFormattedMonthlyPaymentDate,
} from "@app/postBill/utils";
import { convertBalance } from "@app/postBill/utils/convertBalance";

const messages = defineMessages({
  paymentPlanSummaryCardPaidToDate: {
    id: "paymentPlanSummaryCardPaidToDate",
    defaultMessage: "Paid to date",
  },
  paymentPlanSummaryCardRemainingBalance: {
    id: "paymentPlanSummaryCardRemainingBalance",
    defaultMessage: "Remaining balance",
  },
  paymentPlanSummaryCardOrderTotal: {
    id: "paymentPlanSummaryCardOrderTotal",
    defaultMessage: "Order total",
  },
  paymentPlanSummaryCardTestName: {
    id: "paymentPlanSummaryCardTestName",
    defaultMessage: "Test names",
  },
  paymentPlanSummaryCardPlanStartDate: {
    id: "paymentPlanSummaryCardPlanStartDate",
    defaultMessage: "Plan start date",
  },
  paymentPlanSummaryCardPaymentDate: {
    id: "paymentPlanSummaryCardPaymentDate",
    defaultMessage: "Payment date",
  },
  paymentPlanSummaryCardViewStatement: {
    id: "paymentPlanSummaryCardViewStatement",
    defaultMessage: "View statement",
  },
  paymentPlanSummaryCardDynamicMonthlyPaymentDate: {
    id: "paymentPlanSummaryCardDynamicMonthlyPaymentDate",
    defaultMessage: "{day} of each month",
  },
});

interface PaymentPlanSummaryCardProps {
  paid: number;
  remaining: number;
  total: number;
  currency: string;
  testName: string;
  planStartDate: string;
  paymentDate: string;
}

const PaymentPlanSummaryCard: React.FC<PaymentPlanSummaryCardProps> = ({
  paid,
  remaining,
  total,
  currency,
  testName,
  planStartDate,
  paymentDate,
}) => {
  const intl = useIntl();
  const paidPercent = Math.round((paid / total) * 100);
  const getPaymentDate = (paymentDate: string): string => {
    return intl.formatMessage(
      messages.paymentPlanSummaryCardDynamicMonthlyPaymentDate,
      {
        day: getFormattedMonthlyPaymentDate(intl, new Date(paymentDate)),
      }
    );
  };
  return (
    <div className="payment-plan-summary-card">
      <div className="payment-plan-summary-card__header">
        <div className="payment-plan-summary-card__paid">
          <div className="payment-plan-summary-card__paid-label">
            {intl.formatMessage(messages.paymentPlanSummaryCardPaidToDate)}
          </div>
          <div className="payment-plan-summary-card__paid-value">
            {convertBalance(paid, true, currency)}
          </div>
        </div>
        <div className="payment-plan-summary-card__remaining-balance">
          <div className="payment-plan-summary-card__paid-label">
            {intl.formatMessage(
              messages.paymentPlanSummaryCardRemainingBalance
            )}
          </div>
          <div className="payment-plan-summary-card__paid-value">
            {convertBalance(remaining, false, currency)}
          </div>
        </div>
      </div>

      <div className="payment-plan-summary-card__progress">
        <div
          className="payment-plan-summary-card__bar"
          style={{ width: `${paidPercent}%` }}
        />
      </div>

      <div className="payment-plan-summary-card__details">
        <div className="payment-plan-summary-card__row">
          <span>
            {intl.formatMessage(messages.paymentPlanSummaryCardOrderTotal)}
          </span>
          <span>{convertBalance(total, true, currency)}</span>
        </div>
        <div className="payment-plan-summary-card__row">
          <span>
            {intl.formatMessage(messages.paymentPlanSummaryCardTestName)}
          </span>
          <span>{formatFullTestNames(testName)}</span>
        </div>
        <div className="payment-plan-summary-card__row">
          <span>
            {intl.formatMessage(messages.paymentPlanSummaryCardPlanStartDate)}
          </span>
          <span>
            {intl.formatDate(planStartDate, {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </span>
        </div>
        <div className="payment-plan-summary-card__row">
          <span>
            {intl.formatMessage(messages.paymentPlanSummaryCardPaymentDate)}
          </span>
          <span>{getPaymentDate(paymentDate)}</span>
        </div>
      </div>
      {/*TO DO Show view statement button and add function after BE is implemented*/}
      {/* <button
        className="payment-plan-summary-card__button"
        onClick={onViewStatement}
      >
        <Svg
          content={MagnifierIcon}
          className="payment-plan-summary-card__button-icon"
        />
        {intl.formatMessage(messages.paymentPlanSummaryCardViewStatement)}
      </button> */}
    </div>
  );
};

export default PaymentPlanSummaryCard;
